import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { clients } from '../../api/client'
import PageHeader from '../../components/PageHeader'
import { PageLayout, Card, TextInput, Select, TextArea, Button, ErrorMessage, SkeletonForm, Checkbox } from '../../components/ui'
import { trainingTypes } from '../../utils/constants'

const SERVICE_TIERS = [
  { value: '', label: 'Not set' },
  { value: 'basic', label: 'Basic' },
  { value: 'standard', label: 'Standard' },
  { value: 'premium', label: 'Premium' },
  { value: 'elite', label: 'Elite' },
]

const GENDER_OPTIONS = [
  { value: '', label: 'Not specified' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
]

export default function ClientEditPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isNew = !id

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    gender: '',
    dateOfBirth: '',
    trainingType: 'in_person',
    goals: '',
    medicalNotes: '',
    serviceTier: '',
    portalEnabled: true,
    isActive: true,
  })
  const [error, setError] = useState('')

  const { data: client, isLoading: isLoadingClient } = useQuery({
    queryKey: ['client', id],
    queryFn: () => clients.get(id),
    enabled: !isNew,
  })

  useEffect(() => {
    if (client) {
      setFormData({
        firstName: client.firstName || '',
        lastName: client.lastName || '',
        email: client.email || '',
        phone: client.phone || '',
        gender: client.gender || '',
        dateOfBirth: client.dateOfBirth ? client.dateOfBirth.split('T')[0] : '',
        trainingType: client.trainingType || 'in_person',
        goals: client.goals || '',
        medicalNotes: client.medicalNotes || '',
        serviceTier: client.serviceTier || '',
        portalEnabled: client.portalEnabled !== false,
        isActive: client.isActive !== false,
      })
    }
  }, [client])

  const saveMutation = useMutation({
    mutationFn: (data) => {
      // Format dateOfBirth for the backend
      const payload = { ...data }
      if (payload.dateOfBirth === '') {
        delete payload.dateOfBirth
      }
      if (payload.serviceTier === '') {
        delete payload.serviceTier
      }
      if (payload.gender === '') {
        delete payload.gender
      }
      return isNew ? clients.create(payload) : clients.update(id, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      if (!isNew) queryClient.invalidateQueries({ queryKey: ['client', id] })
      navigate(isNew ? '/clients' : `/clients/${id}`)
    },
    onError: (err) => {
      setError(err.message || 'Failed to save client')
    },
  })

  function handleSubmit(e) {
    e.preventDefault()
    setError('')
    // Clean form data: convert empty strings to null for non-string fields
    // so ERRest doesn't try to parse "" as a date/timestamp
    const cleanedData = {
      ...formData,
      dateOfBirth: formData.dateOfBirth || null,
      gender: formData.gender || null,
      phone: formData.phone || null,
    }
    saveMutation.mutate(cleanedData)
  }

  function handleChange(field) {
    return (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }))
  }

  function handleCheckbox(field) {
    return (e) => setFormData(prev => ({ ...prev, [field]: e.target.checked }))
  }

  if (!isNew && isLoadingClient) {
    return (
      <PageLayout>
        <PageHeader title="Edit Client" showBack />
        <div className="p-4">
          <Card>
            <SkeletonForm fields={8} />
          </Card>
        </div>
      </PageLayout>
    )
  }

  const trainingTypeOptions = Object.entries(trainingTypes).map(([value, config]) => ({
    value,
    label: config.label,
  }))

  const genderOptions = [
    { value: '', label: 'Not specified' },
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
  ]

  return (
    <PageLayout>
      <PageHeader
        title={isNew ? 'New Client' : 'Edit Client'}
        showBack
        backTo={isNew ? '/clients' : `/clients/${id}`}
      />

      <form onSubmit={handleSubmit} className="px-4 py-4 space-y-4">
        <ErrorMessage error={error} />

        {/* Basic Info */}
        <Card>
          <h3 className="text-sm font-semibold text-lume-teal mb-4 tracking-wider uppercase">Basic Information</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <TextInput
                label="First Name"
                value={formData.firstName}
                onChange={handleChange('firstName')}
                required
              />
              <TextInput
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange('lastName')}
                required
              />
            </div>

            <TextInput
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              required
            />

            <TextInput
              label="Phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange('phone')}
              placeholder="e.g., +1 (555) 123-4567"
            />

            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Gender"
                value={formData.gender}
                onChange={handleChange('gender')}
                options={GENDER_OPTIONS}
              />

              <div>
                <label className="block text-sm font-medium text-indices-medium mb-1.5">Date of Birth</label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={handleChange('dateOfBirth')}
                  className="w-full px-4 py-3 bg-dial-black border border-dial-border rounded-xl text-indices-bright focus:outline-none focus:ring-2 focus:ring-lume-teal focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Training & Goals */}
        <Card>
          <h3 className="text-sm font-semibold text-lume-teal mb-4 tracking-wider uppercase">Training & Goals</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Training Type"
                value={formData.trainingType}
                onChange={handleChange('trainingType')}
                options={trainingTypeOptions}
              />

              <Select
                label="Service Tier"
                value={formData.serviceTier}
                onChange={handleChange('serviceTier')}
                options={SERVICE_TIERS}
              />
            </div>

            <TextArea
              label="Goals"
              value={formData.goals}
              onChange={handleChange('goals')}
              rows={3}
              placeholder="Client's fitness goals..."
            />

            <TextArea
              label="Medical Notes"
              value={formData.medicalNotes}
              onChange={handleChange('medicalNotes')}
              rows={3}
              placeholder="Injuries, conditions, medications..."
            />
          </div>
        </Card>

        {/* Status & Access */}
        <Card>
          <h3 className="text-sm font-semibold text-lume-teal mb-4 tracking-wider uppercase">Status & Access</h3>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={handleCheckbox('isActive')}
                className="w-5 h-5 rounded border-dial-border bg-dial-black text-lume-teal focus:ring-lume-teal focus:ring-offset-0"
              />
              <div>
                <span className="text-sm font-medium text-indices-bright">Active Client</span>
                <p className="text-xs text-indices-muted">Inactive clients won't appear in active lists</p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.portalEnabled}
                onChange={handleCheckbox('portalEnabled')}
                className="w-5 h-5 rounded border-dial-border bg-dial-black text-lume-teal focus:ring-lume-teal focus:ring-offset-0"
              />
              <div>
                <span className="text-sm font-medium text-indices-bright">Portal Access</span>
                <p className="text-xs text-indices-muted">Allow client to log in to the client portal</p>
              </div>
            </label>
          </div>
        </Card>

        <Button
          type="submit"
          fullWidth
          loading={saveMutation.isPending}
        >
          {isNew ? 'Create Client' : 'Save Changes'}
        </Button>
      </form>
    </PageLayout>
  )
}
